import { Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import * as React from "react";

import { ContextStore } from "../store/contextStore";
import ImageActionButton from "./ImageActionButton";
import images from "./images";

const imageSize = 180;

const useUploadImageStyles = makeStyles(theme => ({
    root: {
        cursor: 'pointer',
        position: 'relative',
        textAlign: 'center',
        marginBottom: theme.spacing(2),
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    image: {
        border: '1px solid transparent',
        borderRadius: '90px',
        opacity: 0.7,
        '&:hover': {
            opacity: 1.0
        }
    },
    imageError: {
        borderColor: theme.palette.secondary.main,
    },
    placeholder: {
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center center",
    },
    upload: {
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center center",
    },
    existing: {
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center center",        
    },
    helperText: {
        color: theme.palette.secondary.main,
        marginTop: theme.spacing(1),
    }
}));

const UploadImageButton = React.memo((props) => {
    const { settings } = React.useContext(ContextStore);

    const classes = useUploadImageStyles();
    // CP-5556: the dashboard's one empty-image placeholder (Martina picked it over image_placeholder.png).
    const PLACEHOLDER_FILE = '/ImageUploadPlaceHolder.svg';
    const preset = "cover_350x250";
    const TYPE_DEFAULT = 0; // image placeholder
    const TYPE_EXISTING = 1; // existing image preview
    const TYPE_UPLOAD = 2; // upload preview
    const type = (props.isUpload && props.image) ? TYPE_UPLOAD : (props.image ? TYPE_EXISTING : TYPE_DEFAULT);

    const imageUrl = React.useMemo(() => (
        type === TYPE_UPLOAD
            ? URL.createObjectURL(props.image)
            : (type === TYPE_EXISTING
                ? images.url(settings.images.base, props.image, preset)
                : null)
    ), [type, props.image, settings.images.base]);

    const imageWidth = props.width || imageSize;
    const imageHeight = props.height || imageSize;
    const borderRadius = props.borderRadius || Math.round(Math.min(imageWidth, imageHeight) / 2);

    const bgImage = {
        backgroundImage: `url(${imageUrl})`,
        height: `${imageHeight}px`,
        width: `${imageWidth}px`,
        borderRadius: borderRadius,
        ...props.imageStyle,
    };
    const placeholderImage = {
        backgroundImage: `url(${process.env.PUBLIC_URL}${PLACEHOLDER_FILE})`,
        height: props.customPlaceHolderImage ? `65vh` : `${imageHeight}px`,
        width: `${imageWidth}px`,
        borderRadius: borderRadius,
        ...props.placeholderStyle
    };

    const rootStyle = props.rootStyle || {};

    return (
        <div className={classes.root} style={rootStyle} onClick={props.disabled ? undefined : props.onClick}>
            {type === TYPE_UPLOAD ?
                <div className={clsx(classes.image, classes.upload, props.error && classes.imageError)} style={bgImage} />
                : null}

            {type === TYPE_EXISTING ?
                <div className={clsx(classes.image, classes.existing, props.error && classes.imageError)} style={bgImage} />
                : null}

            {type === TYPE_DEFAULT ?
                <div className={clsx(classes.image, classes.placeholder, props.error && classes.imageError)} style={placeholderImage} />
                : null}

            {/* CP-5556: imageAction="overlay" puts [UPLOAD IMAGE] / [REPLACE IMAGE] on the image's bottom-right corner,
                "below" centers it under the image. It has no onClick of its own: the click bubbles to the root,
                which opens the picker. Without permission it shows disabled, not hidden. */}
            {props.imageAction ? (
                <ImageActionButton
                    hasImage={type !== TYPE_DEFAULT}
                    overImage={props.imageAction === 'overlay'}
                    disabled={props.disabled}
                    sx={props.imageAction === 'overlay' ? { position: 'absolute', right: 12, bottom: 12 } : { mt: 1 }}
                />
            ) : null}
            {props.helperText ? <div className={classes.helperText}><Typography>{props.helperText}</Typography></div> : null}
        </div>
    );
});

export default UploadImageButton;
